import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import Navbar from "@/components/Navbar";
import { ArrowLeft, ArrowRight, Loader2, Sparkles, Save, Landmark, Rocket, TrendingUp, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import Glossary from "@/components/Glossary";

const PURPOSE_OPTIONS = [
  {
    value: "bank",
    icon: Landmark,
    title: "Bank / finansiering",
    body: "Jeg trenger planen til bank eller annen finansiering.",
    focus: "Kapitalbehov, budsjett, likviditet og tilbakebetalingsevne prioriteres.",
  },
  {
    value: "innovasjon_norge",
    icon: Lightbulb,
    title: "Innovasjon Norge / støtteordninger",
    body: "Jeg skal bruke planen i forbindelse med Innovasjon Norge eller andre støtteordninger.",
    focus: "Innovasjon, markedspotensial, verdiskaping og gjennomføringsevne fremheves.",
  },
  {
    value: "investor",
    icon: TrendingUp,
    title: "Investor / kapitalinnhenting",
    body: "Jeg skal presentere virksomheten for investorer eller hente kapital.",
    focus: "Marked, forretningsmodell, skalerbarhet og vekstpotensial løftes.",
  },
  {
    value: "egen_oppstart",
    icon: Rocket,
    title: "Min egen oppstart",
    body: "Jeg trenger en praktisk plan for å starte og utvikle virksomheten min.",
    focus: "Konkrete steg, kunder, prising og en 12-måneders handlingsplan.",
  },
];

const STEPS = [
  {
    title: "Formål",
    desc: "Hva skal du bruke forretningsplanen til?",
    help: "Vi tilpasser innhold, prioriteringer og anbefalinger etter formålet ditt.",
    fields: [
      { name: "plan_purpose", type: "purpose_choice", options: PURPOSE_OPTIONS },
    ],
  },
  {
    title: "Forretningsidé",
    desc: "Fortell oss kort om idéen din.",
    fields: [
      { name: "business_name", label: "Hva heter (eller skal hete) bedriften?", type: "input", placeholder: "F.eks. Lykke Kaffe AS" },
      { name: "business_idea", label: "I én setning - hva vil du starte?", type: "textarea", placeholder: "Beskriv idéen som om du fortalte en venn" },
      { name: "industry", label: "Hvilken bransje handler det om?", type: "input", placeholder: "F.eks. mat & drikke, IT, helse..." },
      { name: "company_form", label: "Hva slags selskap ønsker du?", type: "input", placeholder: "ENK, AS, eller vet ikke", term: "enk" },
    ],
  },
  {
    title: "Kundene dine",
    desc: "Hvem er du der for?",
    fields: [
      { name: "target_market", label: "Hvem tror du kommer til å kjøpe dette?", type: "textarea", placeholder: "F.eks. Oslo, voksne 25-45, familier..." },
      { name: "customer_profile", label: "Beskriv en typisk kunde med noen få ord", type: "textarea", placeholder: "Alder, interesser, hvorfor de trenger deg" },
    ],
  },
  {
    title: "Produktet ditt",
    desc: "Hva er det du faktisk tilbyr?",
    fields: [
      { name: "product_service", label: "Hva selger du - produkt eller tjeneste?", type: "textarea", placeholder: "Fortell hva folk får når de kjøper hos deg" },
      { name: "problem_solved", label: "Hvilket problem gjør du enklere?", type: "textarea", placeholder: "Hva blir bedre for kunden?" },
      { name: "unique_value", label: "Hva gjør deg annerledes fra alle andre?", type: "textarea", placeholder: "Én ting kundene ikke får hos konkurrentene" },
    ],
  },
  {
    title: "Penger & konkurranse",
    desc: "Hvordan skal det gå rundt?",
    fields: [
      { name: "pricing_model", label: "Hvordan skal du tjene penger?", type: "input", placeholder: "Engangskjøp, abonnement, timepris..." },
      { name: "price_point", label: "Hva vil du ta i pris?", type: "input", placeholder: "F.eks. 49 kr/kopp, 999 kr/mnd" },
      { name: "competitors", label: "Finnes det noen som gjør noe lignende?", type: "textarea", placeholder: "Nevn de du kjenner til - eller si nei hvis du ikke vet" },
    ],
  },
  {
    title: "Mål",
    desc: "Hvor vil du hen?",
    fields: [
      { name: "goals_short", label: "Hva ville vært en suksess det første året?", type: "textarea", placeholder: "Konkret - antall kunder, omsetning, milepæler" },
      { name: "goals_long", label: "Og om 3 år - hvor ser du bedriften?", type: "textarea", placeholder: "Din drømmesituasjon om litt lenger tid" },
    ],
  },
  {
    title: "Hvordan finner de deg",
    desc: "Markedsføring uten unødvendige buzzord.",
    fields: [
      { name: "marketing_channels", label: "Hvor tror du kundene dine bruker tid?", type: "textarea", placeholder: "Instagram, Google, lokalt, nabolaget..." },
    ],
  },
  {
    title: "Kapital",
    desc: "Litt tall - trenger ikke være perfekte.",
    fields: [
      { name: "funding_need", label: "Trenger du kapital for å komme i gang? Hvor mye?", type: "input", placeholder: "F.eks. 250 000 kr - eller '0 kr' hvis du starter uten" },
      { name: "funding_use", label: "Hva ville pengene gått til?", type: "textarea", placeholder: "Utstyr, markedsføring, lønn i første måneder..." },
      { name: "monthly_revenue_estimate", label: "Grovt anslag - hva kunne du tjent per måned?", type: "input", placeholder: "F.eks. 80 000 kr" },
      { name: "monthly_cost_estimate", label: "Og hva ville månedlige kostnader vært?", type: "input", placeholder: "F.eks. 55 000 kr" },
    ],
  },
];

export default function PlanWizard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [inputs, setInputs] = useState({});
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/plans/${id}`);
        setPlan(data);
        setInputs(data.inputs || {});
      } catch {
        toast.error("Plan ikke funnet");
        navigate("/dashboard");
      }
    })();
  }, [id, navigate]);

  const step = STEPS[stepIdx];
  const progress = ((stepIdx + 1) / (STEPS.length + 1)) * 100;

  const setField = (name, value) => setInputs((s) => ({ ...s, [name]: value }));

  // Krever at plan_purpose er valgt før man kan gå videre fra første steg
  const purposeMissing = stepIdx === 0 && !inputs.plan_purpose;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/plans/${id}`, { inputs });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kunne ikke lagre");
    } finally {
      setSaving(false);
    }
  };

  const next = async () => {
    if (purposeMissing) {
      toast.error("Velg hva du skal bruke planen til for å gå videre.");
      return;
    }
    await save();
    if (stepIdx < STEPS.length - 1) setStepIdx(stepIdx + 1);
    else generate();
  };

  const back = () => {
    if (stepIdx > 0) setStepIdx(stepIdx - 1);
  };

  const generate = async () => {
    setGenerating(true);
    try {
      await save();
      await api.post(`/plans/${id}/generate`);
      // Poll for completion - bruk while-loop så throw inne i løkken fanges av ytre try/catch
      const start = Date.now();
      while (true) {
        const { data } = await api.get(`/plans/${id}`);
        if (data.status === "generated") {
          toast.success("Forretningsplanen er klar!");
          navigate(`/plan/${id}`);
          return;
        }
        if (data.status === "error") {
          throw new Error(data.generation_error || "Generering feilet");
        }
        if (Date.now() - start > 180000) {
          throw new Error("Generering tok for lang tid. Prøv igjen.");
        }
        await new Promise((r) => setTimeout(r, 3000));
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Kunne ikke generere planen");
      setGenerating(false);
    }
  };

  if (!plan) {
    return (
      <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-6">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900" data-testid="wizard-back-dashboard">
            <ArrowLeft className="h-4 w-4" /> Til dashbord
          </Link>
          <p className="text-xs text-slate-500">Spørsmål {stepIdx + 1} av {STEPS.length}</p>
        </div>

        <Progress value={progress} className="h-1.5" data-testid="wizard-progress" />

        <div className="mt-12">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500" data-testid={`wizard-step-name-${stepIdx}`}>{step.title}</p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-medium tracking-tight text-slate-900">{step.desc}</h1>
          {step.help && (
            <p className="mt-3 text-slate-500 max-w-xl">{step.help}</p>
          )}
          {stepIdx === 0 && !step.help && (
            <p className="mt-3 text-slate-500 max-w-xl">Svar på noen enkle spørsmål på norsk. Ingen forretningsspråk kreves - vi oversetter til profesjonell tekst for deg.</p>
          )}
        </div>

        <Card className="mt-10 bg-white border border-gray-200 rounded-2xl">
          <CardContent className="p-8 sm:p-10 space-y-6">
            {step.fields.map((f) => (
              <div key={f.name}>
                {f.type === "purpose_choice" ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="wizard-purpose-choices">
                    {f.options.map((opt) => {
                      const Icon = opt.icon;
                      const selected = inputs[f.name] === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setField(f.name, opt.value)}
                          data-testid={`wizard-purpose-${opt.value}`}
                          className={`text-left p-5 rounded-xl border transition-all ${
                            selected
                              ? "border-slate-900 bg-slate-900 text-white ring-2 ring-slate-900/20"
                              : "border-gray-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              selected ? "bg-white/15" : "bg-slate-100"
                            }`}>
                              <Icon className={`h-4 w-4 ${selected ? "text-white" : "text-slate-700"}`} />
                            </div>
                            <div className="flex-1">
                              <p className={`font-medium leading-tight ${selected ? "text-white" : "text-slate-900"}`}>
                                {opt.title}
                              </p>
                              <p className={`mt-1.5 text-sm leading-relaxed ${selected ? "text-white/85" : "text-slate-600"}`}>
                                {opt.body}
                              </p>
                              <p className={`mt-2 text-xs leading-relaxed ${selected ? "text-white/70" : "text-slate-500"}`}>
                                {opt.focus}
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <>
                    <Label htmlFor={f.name} className="text-sm font-medium text-slate-700 inline-flex items-center gap-1.5">
                      <span>{f.label}</span>
                      {f.term && <Glossary term={f.term} />}
                    </Label>
                    {f.type === "textarea" ? (
                      <Textarea
                        id={f.name}
                        placeholder={f.placeholder}
                        value={inputs[f.name] || ""}
                        onChange={(e) => setField(f.name, e.target.value)}
                        className="mt-2 min-h-[110px] rounded-xl"
                        data-testid={`wizard-field-${f.name}`}
                      />
                    ) : (
                      <Input
                        id={f.name}
                        placeholder={f.placeholder}
                        value={inputs[f.name] || ""}
                        onChange={(e) => setField(f.name, e.target.value)}
                        className="mt-2 h-11 rounded-xl"
                        data-testid={`wizard-field-${f.name}`}
                      />
                    )}
                  </>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="mt-8 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={back}
            disabled={stepIdx === 0}
            className="rounded-full h-11"
            data-testid="wizard-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Forrige
          </Button>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={save}
              disabled={saving}
              className="rounded-full h-11"
              data-testid="wizard-save-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Lagre</>}
            </Button>
            {stepIdx < STEPS.length - 1 ? (
              <Button
                onClick={next}
                disabled={purposeMissing}
                className="rounded-full h-11 bg-slate-900 hover:bg-slate-800 px-6 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="wizard-next-btn"
              >
                Neste <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={generate}
                disabled={generating}
                className="rounded-full h-11 bg-slate-900 hover:bg-slate-800 px-6"
                data-testid="wizard-generate-btn"
              >
                {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Genererer plan...</> : <><Sparkles className="h-4 w-4 mr-2" /> Generer plan</>}
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
